import {
  QAlert,
  QButton,
  QCharacterCount,
  QFormControl,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QStack,
  QText,
  QTextarea,
} from '@qualio/ui-components';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChangedApprovalUsers } from './ChangedApprovalUsers';
import { UniversalUserSelectionType, User } from '../../../../../../types';

export type ManageApprovalUsersConfirmationModalProps = {
  isOpen: boolean;
  modalTitle: string;
  isReviewers: boolean;
  addedApprovalUsers: UniversalUserSelectionType[];
  removedApprovalUsers: UniversalUserSelectionType[];
  selections: readonly User[];
  requiresComment?: boolean;
  displayWarning: boolean;
  onClose: () => void;
  onPrevious: () => void;
  onSubmit: (selections: readonly User[], comment: string) => Promise<void>;
};

export const ManageApprovalUsersConfirmationModal = ({
  isOpen,
  modalTitle,
  isReviewers,
  addedApprovalUsers = [],
  removedApprovalUsers = [],
  selections = [],
  requiresComment = false,
  displayWarning = false,
  onClose,
  onSubmit,
  onPrevious,
}: ManageApprovalUsersConfirmationModalProps) => {
  const { t } = useTranslation();
  const [isApplyingChanges, setIsApplyingChanges] = useState(false);
  const userTypeText = useMemo(() => (isReviewers ? 'reviewers' : 'approvers'), [isReviewers]);

  const [reason, setReason] = useState('');

  const close = useCallback(() => {
    setReason('');
    onClose();
  }, [onClose]);

  const applyChanges = useCallback(async () => {
    setIsApplyingChanges(true);
    await onSubmit(selections, reason);
    setIsApplyingChanges(false);
  }, [onSubmit, reason, selections]);

  return (
    <QModal isOpen={isOpen} onClose={close}>
      <QModalHeader>
        <QText fontSize="xl">{modalTitle}</QText>
      </QModalHeader>
      <QModalBody>
        <QStack spacing="6">
          {displayWarning && (
            <QAlert
              title={t('events.messages.thisActionAffectsStepApprovalStatus')}
              description={<QText>{t('events.messages.removingApproversWarning')}</QText>}
              status="warning"
            />
          )}
          <ChangedApprovalUsers
            users={addedApprovalUsers}
            label={isReviewers ? t('common.filters.reviewersAdded') : t('common.filters.approversAdded')}
          />
          <ChangedApprovalUsers
            users={removedApprovalUsers}
            label={isReviewers ? t('common.filters.reviewersRemoved') : t('common.filters.approversRemoved')}
          />
          {requiresComment && (
            <QFormControl label={t('common.labels.reason')} isRequired>
              <QTextarea
                data-cy={`manage-${userTypeText}-modal-comment`}
                placeholder={t('common.filters.reasonForRemovingTheUserType', { userType: userTypeText })}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              >
                <QCharacterCount max={1000} />
              </QTextarea>
            </QFormControl>
          )}
        </QStack>
      </QModalBody>
      <QModalActions>
        <QButton
          data-cy={`event-form-manage-${userTypeText}-cancel-button`}
          variant="ghost"
          isDisabled={isApplyingChanges}
          onClick={close}
        >
          {t('common.actions.cancel')}
        </QButton>
        <QButton
          data-cy={`event-form-manage-${userTypeText}-previous-button`}
          variant="outline"
          isDisabled={isApplyingChanges}
          onClick={onPrevious}
        >
          {t('common.labels.back')}
        </QButton>
        <QButton
          data-cy={`event-form-manage-users-apply-changes-button`}
          isLoading={isApplyingChanges}
          isDisabled={(requiresComment && !reason) || isApplyingChanges}
          onClick={applyChanges}
        >
          {t('common.actions.applyChanges')}
        </QButton>
      </QModalActions>
    </QModal>
  );
};
